// =====================================================
// UTILS: Date
// Description: Utilitaires pour formater les dates
// =====================================================

/**
 * Formater une date en temps relatif ("Il y a 2h", "Hier à 14:30", etc.)
 */
export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Moins d'une minute
  if (diffMs < 60000) {
    return "À l'instant";
  }

  // Moins d'une heure
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) {
    return `Il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
  }

  // Moins de 24 heures
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  }

  // Hier
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);

  if (dateOnly.getTime() === yesterday.getTime()) {
    const timeStr = new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
    return `Hier à ${timeStr}`;
  }

  // Moins de 7 jours
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays < 7) {
    const timeStr = new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
    return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''} à ${timeStr}`;
  }

  // Plus ancien : format complet
  return formatDate(dateString);
}

/**
 * Formater une date en format français complet
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);

  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Formater une date en format court (jour/mois/année)
 */
export function formatDateShort(dateString: string): string {
  const date = new Date(dateString);

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/**
 * Formater une date d'échéance de tâche de manière intelligente
 * - Aujourd'hui → "Aujourd'hui"
 * - Demain → "Demain"
 * - Autre → "DD/MM/YYYY"
 * - Si heure spécifiée (≠ 12:00 et ≠ 23:59) → Ajouter ", à HH:MM"
 */
export function formatTaskDueDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  // Réinitialiser les heures pour comparer juste les dates
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const taskDate = new Date(date);
  taskDate.setHours(0, 0, 0, 0);

  // Déterminer le label de jour
  let dayLabel = '';
  if (taskDate.getTime() === today.getTime()) {
    dayLabel = "Aujourd'hui";
  } else if (taskDate.getTime() === tomorrow.getTime()) {
    dayLabel = 'Demain';
  } else {
    // Format DD/MM/YYYY
    dayLabel = new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  // Vérifier si une heure spécifique est définie
  // On vérifie en UTC car les dates sont stockées en UTC
  // Heures par défaut : 12:00 UTC (nouveau) ou 23:59 UTC (ancien)
  const hoursUTC = date.getUTCHours();
  const minutesUTC = date.getUTCMinutes();
  const isDefaultTime =
    (hoursUTC === 12 && minutesUTC === 0) || (hoursUTC === 23 && minutesUTC === 59);

  if (!isDefaultTime) {
    const timeStr = new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
    return `${dayLabel}, à ${timeStr}`;
  }

  return dayLabel;
}
